import React from 'react';
import { Link } from 'react-router-dom';

const Landing = () => {
  return (
    <div className="landing-page">
      <div className="landing-container">
        {/* Navigation */}
        <nav className="landing-nav">
          <Link to="/" className="landing-logo">
            <span className="landing-logo-icon">R</span>
            <span className="landing-logo-text">
              <span>RiseMyRank</span>
              <span className="landing-club-stamp">.club</span>
            </span>
          </Link>
          <div className="landing-nav-links">
            <a href="#features" className="landing-nav-link">Features</a>
            <a href="#pricing" className="landing-nav-link">Pricing</a>
            <Link to="/login" className="landing-btn landing-btn-secondary">Login</Link>
            <Link to="/signup" className="landing-btn landing-btn-primary">Get Started</Link>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="landing-hero">
          <div className="landing-hero-badge">
            ✨ The Gold Standard in Digital Business Cards
          </div>
          <h1 className="landing-hero-title">
            Your Professional Identity,
            <br />
            <span className="landing-gold-text">Elevated</span>
          </h1>
          <p className="landing-hero-subtitle">
            Create a stunning, shareable digital business card that makes an unforgettable impression. 
            Premium design meets effortless networking.
          </p>
          <div className="landing-hero-cta">
            <Link to="/signup" className="landing-btn landing-btn-primary landing-btn-large">
              Create Your Card
            </Link>
            <a href="#features" className="landing-btn landing-btn-secondary landing-btn-large">
              See Features
            </a>
          </div>
        </section>

        {/* Feature 1: Instant Setup */}
        <section id="features" className="landing-feature-section">
          <div className="landing-feature-split">
            <div className="landing-feature-content">
              <div className="landing-feature-label">Setup in Minutes</div>
              <h2 className="landing-feature-title">Launch Your Professional Presence</h2>
              <p className="landing-feature-description">
                No design skills required. Our intuitive dashboard lets you create a premium digital 
                business card in minutes. Choose your colors, add your links, and share instantly.
              </p>
              <ul className="landing-feature-list">
                <li>Drag-and-drop link management</li>
                <li>Real-time preview of your card</li>
                <li>Custom branding with glow effects</li>
                <li>One-click sharing via unique URL</li>
              </ul>
              <Link to="/signup" className="landing-btn landing-btn-primary">
                Get Started →
              </Link>
            </div>
            <div className="landing-feature-visual">
              <PreviewCard 
                name="Alex Morgan"
                bio="Digital Marketing Strategist | Helping brands grow online"
                glowColor="#d4af37"
                links={[
                  { title: "Portfolio", url: "#" },
                  { title: "LinkedIn", url: "#" },
                  { title: "Schedule Meeting", url: "#" }
                ]}
              />
            </div>
          </div>
        </section>

        {/* Feature 2: Professional Design */}
        <section className="landing-feature-section">
          <div className="landing-feature-split landing-reverse">
            <div className="landing-feature-content">
              <div className="landing-feature-label">Premium Design</div>
              <h2 className="landing-feature-title">Make a Lasting Impression</h2>
              <p className="landing-feature-description">
                Stand out with a sophisticated dark-mode design that exudes professionalism. 
                Customizable glow effects and golden accents ensure your card is unforgettable.
              </p>
              <ul className="landing-feature-list">
                <li>Luxury dark theme with gold accents</li>
                <li>Customizable glow intensity</li>
                <li>Mobile-responsive design</li>
                <li>Lightning-fast loading</li>
              </ul>
              <Link to="/signup" className="landing-btn landing-btn-primary">
                See Pricing →
              </Link>
            </div>
            <div className="landing-feature-visual">
              <PreviewCard 
                name="Jordan Lee"
                bio="CEO & Founder | Building the future of SaaS"
                glowColor="#60a5fa"
                links={[
                  { title: "Company Website", url: "#" },
                  { title: "Book a Call", url: "#" }
                ]}
              />
            </div>
          </div>
        </section>

        {/* Feature 3: Share Anywhere */}
        <section className="landing-feature-section">
          <div className="landing-feature-split">
            <div className="landing-feature-content">
              <div className="landing-feature-label">Share Instantly</div>
              <h2 className="landing-feature-title">Your Card, Everywhere</h2>
              <p className="landing-feature-description">
                Share your digital business card with a simple link. Add it to your email signature, 
                social media profiles, or share via QR code. Update once, reflect everywhere.
              </p>
              <ul className="landing-feature-list">
                <li>Unique vanity URL (risemyrank.club/yourname)</li>
                <li>Email & phone contact buttons</li>
                <li>Analytics to track card views</li>
                <li>Update anytime without reprinting</li>
              </ul>
              <Link to="/signup" className="landing-btn landing-btn-primary">
                Claim Your URL →
              </Link>
            </div>
            <div className="landing-feature-visual">
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '1rem',
                padding: '2rem',
                background: 'var(--grey-dark)',
                borderRadius: '12px',
                border: '1px solid var(--grey-medium)'
              }}>
                <div style={{ 
                  fontFamily: 'var(--font-mono)', 
                  fontSize: '1.1rem',
                  color: 'var(--gold)',
                  padding: '1rem',
                  background: 'var(--grey-darkest)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid var(--gold)'
                }}>
                  risemyrank.club/yourname
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem'
                }}>
                  <div style={{
                    padding: '1rem',
                    background: 'var(--grey-medium)',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>
                    📧 Email
                  </div>
                  <div style={{
                    padding: '1rem',
                    background: 'var(--grey-medium)',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '0.9rem'
                  }}>
                    📱 Call
                  </div>
                </div>
                <div style={{
                  padding: '0.75rem',
                  background: 'var(--grey-medium)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}>
                  🔗 View All Links
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="landing-feature-section">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <div className="landing-feature-label">Simple Pricing</div>
            <h2 className="landing-feature-title">One Plan, Unlimited Potential</h2>
            <div style={{ 
              background: 'var(--grey-darkest)',
              border: '2px solid var(--gold)',
              borderRadius: '16px',
              padding: '3rem',
              marginTop: '2rem'
            }}>
              <div style={{ fontSize: '3rem', fontWeight: '700', color: 'var(--gold)' }}>
                $2.99<span style={{ fontSize: '1.5rem', fontWeight: '400', color: 'var(--white-dim)' }}>/month</span>
              </div>
              <p style={{ fontSize: '1.1rem', color: 'var(--white-dim)', marginTop: '1rem' }}>
                Everything you need to create, manage, and share your premium digital business card
              </p>
              <ul className="landing-feature-list" style={{ 
                textAlign: 'left', 
                maxWidth: '400px', 
                margin: '2rem auto' 
              }}>
                <li>Custom vanity URL</li>
                <li>Unlimited links</li>
                <li>Real-time updates</li>
                <li>Premium gold theme</li>
                <li>Analytics dashboard</li>
                <li>Priority support</li>
              </ul>
              <Link to="/signup" className="landing-btn landing-btn-primary landing-btn-large">
                Start Your Free Trial
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="landing-footer">
          <p>&copy; 2026 RiseMyRank.club. All rights reserved.</p>
          <div className="landing-footer-links">
            <a href="#" className="landing-footer-link">Privacy Policy</a>
            <a href="#" className="landing-footer-link">Terms of Service</a>
            <a href="#" className="landing-footer-link">Contact Us</a>
          </div>
        </footer>
      </div>
    </div>
  );
};

// Preview Card Component for Features
const PreviewCard = ({ name, bio, glowColor, links }) => {
  return (
    <div style={{
      background: 'var(--grey-dark)',
      border: '2px solid var(--grey-medium)',
      borderRadius: '16px',
      padding: '2rem',
      textAlign: 'center',
      boxShadow: `0 0 40px ${glowColor}40`,
      maxWidth: '350px',
      margin: '0 auto'
    }}>
      <div style={{
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${glowColor}, ${glowColor}dd)`,
        margin: '0 auto 1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2.5rem',
        fontWeight: '700',
        color: 'var(--black)',
        boxShadow: `0 4px 24px ${glowColor}60`
      }}>
        {name.charAt(0)}
      </div>
      <h3 style={{
        fontFamily: 'var(--font-display)',
        fontSize: '1.5rem',
        fontWeight: '700',
        marginBottom: '0.5rem'
      }}>
        {name}
      </h3>
      <p style={{
        color: 'var(--white-dim)',
        marginBottom: '1.5rem',
        fontSize: '0.9rem',
        lineHeight: '1.5'
      }}>
        {bio}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {links.map((link, idx) => (
          <div key={idx} style={{
            padding: '0.75rem',
            background: 'var(--grey-medium)',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.9rem',
            transition: 'all 0.2s ease'
          }}>
            {link.title}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Landing;
